import React, { useState, useEffect, useMemo, useCallback, useRef, startTransition } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Edit, Eye, XCircle, Search, DollarSign, AlertTriangle,
  Layers, GripVertical, Loader2, Ban, ChevronUp, ChevronDown, CheckCircle2
} from "lucide-react";
import { format } from "date-fns";

import { API } from "@/config/api";
import { AREAS, REP_DIRECTORY, getRepDisplayName } from "@/config/constants";
import StageModal, { computeStageCompletion } from "@/components/StageModal";
import {
  STAGE_CONFIG_MAP, SS_STAGES, TT_STAGES, SS_STAGE_COLORS, TT_STAGE_COLORS, LEGACY_STAGE_MAP
} from "@/config/stageConfig";
import { useSearchParams } from "react-router-dom";
import { useRealtime } from "@/context/RealtimeContext";
import { LiveEditors } from "@/components/LiveEditors";

// 10 backend pipeline stages (order matters — left to right)
const PIPELINE_STAGES = [
  "Referrals",
  "Pre SS",
  "SS Authorization",
  "SS Facilitation",
  "Post SS",
  "SS Reports",
  "TT Facilitation",
  "Post TT",
  "TT Sum",
  "TT Report",
  "Lost/Cancelled",
];

const STAGE_COLORS = {
  ...SS_STAGE_COLORS,
  ...TT_STAGE_COLORS,
  "Referrals": { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-800", accent: "bg-slate-500" },
  "Pre SS": { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-800", accent: "bg-blue-500" },
  "SS Authorization": { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-800", accent: "bg-purple-500" },
  "SS Facilitation": { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-800", accent: "bg-amber-500" },
  "Post SS": { bg: "bg-teal-100", border: "border-teal-300", text: "text-teal-800", accent: "bg-teal-500" },
  "SS Reports": { bg: "bg-cyan-100", border: "border-cyan-300", text: "text-cyan-800", accent: "bg-cyan-500" },
  "TT Facilitation": { bg: "bg-indigo-100", border: "border-indigo-300", text: "text-indigo-800", accent: "bg-indigo-500" },
  "Post TT": { bg: "bg-pink-100", border: "border-pink-300", text: "text-pink-800", accent: "bg-pink-500" },
  "TT Sum": { bg: "bg-rose-100", border: "border-rose-300", text: "text-rose-800", accent: "bg-rose-500" },
  "TT Report": { bg: "bg-green-100", border: "border-green-300", text: "text-green-800", accent: "bg-green-500" },
  "Lost/Cancelled": { bg: "bg-red-100", border: "border-red-300", text: "text-red-800", accent: "bg-red-500" },
};

// Stage -> pipeline_cases date field (backend field names)
const ALL_STAGE_COLORS = { ...SS_STAGE_COLORS, ...TT_STAGE_COLORS };

const STAGE_DATE_FIELD = {
  "Referrals": "referral_date",
  "Pre SS": "pre_ss_date",
  "SS Authorization": "ss_auth_date",
  "SS Facilitation": "ss_facilitation_date",
  "Post SS": "post_ss_date",
  "SS Reports": "ss_reports_date",
  "TT Facilitation": "tt_facilitation_date",
  "Post TT": "post_tt_date",
  "TT Sum": "tt_sum_date",
  "TT Report": "tt_report_date",
  "Lost/Cancelled": "lost_cancelled_date",
  "Referral": "referral_date",
  "SS Schedule": "ss_schedule_date",
  "SS Appointment Confirmation": "ss_appointment_confirmation_date",
  "SS Follow up": "ss_followup_date",
  "SS Feedback": "ss_feedback_date",
  "TT Schedule": "tt_schedule_date",
  "TT Appointment Confirmation": "tt_appointment_confirmation_date",
  "TT Next day FU": "tt_nextday_fu_date",
  "TT Feedback": "tt_feedback_date",
  "Done": "done_date",
};

// Column a case belongs to (legacy 10-stage names mapped onto the new columns)
const boardStage = (c) => {
  const stage = LEGACY_STAGE_MAP[c.current_stage] || c.current_stage;
  // SS Feedback now opens the Titration pipeline, so SS cases left in it show under SS Follow up
  if (stage === "SS Feedback" && c.current_pipeline !== "TT") return "SS Follow up";
  return stage;
};

const today = () => new Date().toISOString().split("T")[0];

const repName = (c) => getRepDisplayName(c.assigned_rep_name || c.assigned_rep || "") || "Unassigned";

const stageDateOf = (c) => c[STAGE_DATE_FIELD[c.current_stage]] || c.referral_date || (c.created_at ? String(c.created_at).split("T")[0] : null);

const getDaysInStage = (c) => {
  const d = stageDateOf(c);
  if (!d) return 0;
  const days = Math.floor((new Date() - new Date(d)) / (1000 * 60 * 60 * 24));
  return days < 0 ? 0 : days;
};

const getStatusIndicator = (c) => {
  const days = getDaysInStage(c);
  if (days > 30) return { color: "bg-red-500", label: `${days}d`, title: "Overdue" };
  if (days > 14) return { color: "bg-orange-500", label: `${days}d`, title: "At risk" };
  if (days > 7) return { color: "bg-yellow-500", label: `${days}d`, title: "Monitor" };
  return { color: "bg-emerald-500", label: `${days}d`, title: "On track" };
};

// Unified patient search: exact 5-digit TIN, prefix TIN under 5 digits, otherwise name match
const matchesPatientSearch = (c, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) return true;
  const term = searchTerm.trim();
  const tin = String(c.tin_number || "");
  const fullName = `${c.name || ""} ${c.surname || ""}`.toLowerCase();

  if (/^\d+$/.test(term)) {
    if (term.length === 5) return tin === term;
    if (term.length < 5) return tin.startsWith(term);
    return tin.includes(term);
  }
  return fullName.includes(term.toLowerCase()) || tin.toLowerCase().includes(term.toLowerCase());
};

const fmt = (d) => {
  if (!d) return "—";
  try {
    return format(new Date(d), "MMM d, yyyy");
  } catch {
    return d;
  }
};

// A live update should only replace the cases that really changed — everything else keeps its
// exact object so memoised cards never re-render (no flashing, no lost scroll position).
const mergeCases = (prev, next) => {
  const byTin = new Map(prev.map((c) => [c.tin_number, c]));
  const merged = next.map((incoming) => {
    const existing = byTin.get(incoming.tin_number);
    return existing && JSON.stringify(existing) === JSON.stringify(incoming) ? existing : incoming;
  });
  const identical =
    merged.length === prev.length && merged.every((c, i) => c === prev[i]);
  return identical ? prev : merged;
};

const editorSignature = (editors) =>
  (editors || []).map((e) => `${e.email}|${e.field_id}`).sort().join(",");

const PipelineCard = React.memo(
  ({ c, editors, onDragStart, onDragEnd, onContextMenu, onOpen, onView, onEdit }) => {
    const status = getStatusIndicator(c);
    const stageCfg = STAGE_CONFIG_MAP[boardStage(c)];
    const progress = stageCfg ? computeStageCompletion(stageCfg, c[stageCfg.dataFieldName]) : null;
    return (
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onContextMenu={onContextMenu}
        onClick={onOpen}
        className="group relative bg-white border-2 border-gray-200 rounded-lg p-3 cursor-grab hover:shadow-lg hover:border-blue-400 transition-[box-shadow,border-color]"
        data-testid={`referral-card-${c.tin_number}`}
      >
        <LiveEditors editors={editors} tin={c.tin_number} />
        <div
          className={`${status.color} text-white text-[10px] font-bold rounded-full w-8 h-8 flex items-center justify-center absolute top-2 right-2`}
          title={`${status.title} — ${status.label} in stage`}
          data-testid={`card-status-${c.tin_number}`}
        >
          {status.label}
        </div>

        <div className="flex items-start gap-1 pr-9 mb-2">
          <GripVertical className="h-3.5 w-3.5 mt-1 text-gray-300 group-hover:text-gray-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-[11px] font-bold tracking-wide text-gray-500 uppercase">
              TIN {c.tin_number || "—"}
            </p>
            <p className="font-semibold text-sm text-gray-900 truncate">
              {c.name} {c.surname}
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-600 mb-1">
          <span className="font-semibold">{c.medical_aid || "No medical aid"}</span>
        </p>
        {c.medical_aid_plan && (
          <p className="text-xs text-gray-500 mb-1">Plan: {c.medical_aid_plan}</p>
        )}
        <p className="text-xs text-gray-600 truncate">{c.doctor || "Unknown doctor"}</p>

        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {progress && progress.total > 0 && (
            <Badge
              className={`text-[10px] gap-0.5 hover:bg-inherit ${
                progress.percent === 100
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-amber-100 text-amber-800'
              }`}
              title={`${progress.filled} of ${progress.total} fields captured for ${boardStage(c)}`}
              data-testid={`card-completion-${c.tin_number}`}
            >
              {progress.percent === 100 ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <AlertTriangle className="h-3 w-3" />
              )}
              {progress.filled}/{progress.total} fields
            </Badge>
          )}
          {c.area && <Badge variant="secondary" className="text-[10px]">{c.area}</Badge>}
          <span className="text-xs font-medium text-gray-700">{repName(c)}</span>
          {c.commission_eligible && (
            <Badge className="text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
              <DollarSign className="h-3 w-3 mr-0.5" />Comm
            </Badge>
          )}
          {c.out_of_hospital && <Badge variant="outline" className="text-[10px]">OOH</Badge>}
        </div>

        <div className="text-[11px] text-gray-500 space-y-0.5 border-t mt-2 pt-2">
          <p>Referral: {fmt(c.referral_date)}</p>
          <p>In stage since: {fmt(stageDateOf(c))}</p>
        </div>

        <div className="mt-3 pt-2 border-t flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onView}
            className="flex-1 h-7 text-xs"
            data-testid={`view-referral-${c.tin_number}`}
          >
            <Eye className="h-3 w-3 mr-1" />View
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            className="flex-1 h-7 text-xs"
            data-testid={`edit-referral-${c.tin_number}`}
          >
            <Edit className="h-3 w-3 mr-1" />Edit
          </Button>
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.c === next.c && editorSignature(prev.editors) === editorSignature(next.editors)
);

const PipelinePage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedCase, setDraggedCase] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const pipelineType = (searchParams.get("type") || "ss").toLowerCase() === "tt" ? "tt" : "ss";
  const stages = pipelineType === "tt" ? TT_STAGES : SS_STAGES;
  const stageColors = pipelineType === "tt" ? TT_STAGE_COLORS : SS_STAGE_COLORS;
  // Arriving from a dashboard number: show just that stage, optionally only the month's completions
  const focusStage = stages.includes(searchParams.get("stage")) ? searchParams.get("stage") : null;
  const doneMonth = searchParams.get("done_month") || "";
  const visibleStages = focusStage ? [focusStage] : stages;
  const [showTTDecisionModal, setShowTTDecisionModal] = useState(false);
  const [ttDecisionCase, setTtDecisionCase] = useState(null);

  // Filters
  const [filterRep, setFilterRep] = useState("");
  const [filterMedicalAid, setFilterMedicalAid] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [searchPatient, setSearchPatient] = useState("");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");

  // Reference data + UI state
  const [medicalAids, setMedicalAids] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingCase, setEditingCase] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [currentStageConfig, setCurrentStageConfig] = useState(null);
  const [stageModalCase, setStageModalCase] = useState(null);
  const [targetStagePending, setTargetStagePending] = useState(null);

  const closeStageModal = () => {
    setShowStageModal(false);
    setStageModalCase(null);
    setCurrentStageConfig(null);
    setTargetStagePending(null);
  };

  const openStageModal = (c, targetStage) => {
    const config = STAGE_CONFIG_MAP[targetStage];
    if (!config) return false;
    setStageModalCase(c);
    setCurrentStageConfig(config);
    setTargetStagePending(targetStage);
    setShowStageModal(true);
    return true;
  };
  const [referralModalCase, setReferralModalCase] = useState(null);

  const openReferralModal = (c = null) => {
    setReferralModalCase(c);
    setShowReferralModal(true);
  };

  const boardRef = useRef(null);

  // Remember where everyone is looking before a live update lands
  const captureScroll = useCallback(() => {
    const root = boardRef.current;
    if (!root) return null;
    const cols = {};
    root.querySelectorAll("[data-stage-scroll]").forEach((el) => {
      cols[el.dataset.stageScroll] = el.scrollTop;
    });
    return { left: root.scrollLeft, top: window.scrollY, cols };
  }, []);

  const restoreScroll = useCallback((snapshot) => {
    if (!snapshot) return;
    requestAnimationFrame(() => {
      const root = boardRef.current;
      if (!root) return;
      root.scrollLeft = snapshot.left;
      root.querySelectorAll("[data-stage-scroll]").forEach((el) => {
        const top = snapshot.cols[el.dataset.stageScroll];
        if (top != null) el.scrollTop = top;
      });
      if (window.scrollY !== snapshot.top) window.scrollTo(0, snapshot.top);
    });
  }, []);

  const fetchCases = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      const snapshot = quiet ? captureScroll() : null;
      try {
        const res = await axios.get(`${API}/pipeline/${pipelineType}/cases`, { withCredentials: true });
        const incoming = res.data || [];
        // Quiet refresh = smart diff: only the cases that actually changed get new objects
        setCases((prev) => (quiet ? mergeCases(prev, incoming) : incoming));
        if (quiet) restoreScroll(snapshot);
      } catch (error) {
        console.error("Error fetching cases:", error);
        if (!quiet) toast.error(error.response?.data?.detail || "Failed to load cases");
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [pipelineType, captureScroll, restoreScroll]
  );

  const { onCaseChange, notifyCaseChanged, editorsFor } = useRealtime();

  // Somebody else changed a case — patch the board in place, never reload it from scratch
  useEffect(
    () =>
      onCaseChange((event) => {
        startTransition(() => {
          fetchCases({ quiet: true });
        });
        const who = event.by || "Someone";
        const label = event.patient || event.case_tin;
        if (event.action === "moved" && event.to_stage) {
          toast.info(`${who} moved ${label} to ${event.to_stage}`, { id: `rt-${event.case_tin}` });
        } else if (event.action === "created") {
          toast.info(`${who} added ${label}`, { id: `rt-${event.case_tin}` });
        } else {
          toast.info(`${who} updated ${label}`, { id: `rt-${event.case_tin}` });
        }
      }),
    [onCaseChange, fetchCases]
  );

  useEffect(() => {
    fetchCases();
    (async () => {
      try {
        const res = await axios.get(`${API}/medical-aids`, { withCredentials: true });
        setMedicalAids(res.data || []);
      } catch (error) {
        console.error("Error fetching medical aids:", error);
      }
    })();
    (async () => {
      try {
        const res = await axios.get(`${API}/doctors`, { withCredentials: true });
        setDoctors(res.data || []);
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    })();
  }, [fetchCases]);

  const isLost = (c) => Boolean(c.is_canceled) || c.current_stage === "Lost/Cancelled";
  const activeCases = useMemo(() => cases.filter((c) => !isLost(c)), [cases]);
  const allBoardCases = cases;

  const doctorOptions = useMemo(() => {
    const set = new Set(activeCases.map((c) => c.doctor).filter(Boolean));
    return Array.from(set).sort();
  }, [activeCases]);

  const medicalAidOptions = useMemo(() => {
    const set = new Set([
      ...medicalAids.map((m) => m.medical_aid_name),
      ...activeCases.map((c) => c.medical_aid).filter(Boolean),
    ]);
    return Array.from(set).sort();
  }, [medicalAids, activeCases]);

  const filteredCases = useMemo(() => {
    return allBoardCases.filter((c) => {
      if (filterRep && repName(c) !== filterRep) return false;
      if (filterMedicalAid && c.medical_aid !== filterMedicalAid) return false;
      if (filterDoctor && c.doctor !== filterDoctor) return false;
      if (filterArea && c.area !== filterArea) return false;
      if (!matchesPatientSearch(c, searchPatient)) return false;
      if (dateRangeStart && (!c.referral_date || c.referral_date < dateRangeStart)) return false;
      if (dateRangeEnd && (!c.referral_date || c.referral_date > dateRangeEnd)) return false;
      return true;
    });
  }, [allBoardCases, filterRep, filterMedicalAid, filterDoctor, filterArea, searchPatient, dateRangeStart, dateRangeEnd]);

  const casesByStage = useMemo(() => {
    const grouped = {};
    stages.forEach((s) => {
      const inStage =
        s === "Lost/Cancelled"
          ? filteredCases.filter(isLost)
          : filteredCases.filter((c) => !isLost(c) && boardStage(c) === s);
      grouped[s] =
        s === "Done" && doneMonth
          ? inStage.filter((c) => (c.done_date || c.current_stage_date || "").startsWith(doneMonth))
          : inStage;
    });
    return grouped;
  }, [filteredCases, stages, doneMonth]);

  const stats = useMemo(() => ({
    total: filteredCases.filter((c) => !isLost(c)).length,
    commission: filteredCases.filter((c) => c.commission_eligible).length,
    overdue: filteredCases.filter((c) => !isLost(c) && getDaysInStage(c) > 30).length,
    canceled: cases.filter(isLost).length,
  }), [filteredCases, cases]);

  const clearAllFilters = () => {
    setFilterRep("");
    setFilterMedicalAid("");
    setFilterDoctor("");
    setFilterArea("");
    setSearchPatient("");
    setDateRangeStart("");
    setDateRangeEnd("");
  };

  // ---------- Drag & drop ----------
  // Auto-scroll the board while dragging near an edge so far-off stages can be reached
  const autoScroll = useRef({ direction: 0, frame: null });

  const stopAutoScroll = useCallback(() => {
    if (autoScroll.current.frame) cancelAnimationFrame(autoScroll.current.frame);
    autoScroll.current = { direction: 0, frame: null };
  }, []);

  const runAutoScroll = useCallback(() => {
    const root = boardRef.current;
    const { direction } = autoScroll.current;
    if (!root || !direction) return stopAutoScroll();
    root.scrollLeft += direction * 18;
    autoScroll.current.frame = requestAnimationFrame(runAutoScroll);
  }, [stopAutoScroll]);

  const edgeAutoScroll = useCallback(
    (clientX) => {
      const root = boardRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const edge = 110;
      const direction =
        clientX > rect.right - edge ? 1 : clientX < rect.left + edge ? -1 : 0;
      if (direction === autoScroll.current.direction) return;
      stopAutoScroll();
      autoScroll.current.direction = direction;
      if (direction) autoScroll.current.frame = requestAnimationFrame(runAutoScroll);
    },
    [runAutoScroll, stopAutoScroll]
  );

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const handleDragStart = (e, c) => {
    setDraggedCase(c);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    edgeAutoScroll(e.clientX);
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    stopAutoScroll();
    setDragOverStage(null);
    const c = draggedCase;
    setDraggedCase(null);
    if (!c || c.current_stage === targetStage) return;
    // Dragging only moves the card — the stage form is opened by clicking the card or Edit
    await moveCaseToStage(c.tin_number, targetStage);
  };

  const moveCaseToStage = async (tinNumber, newStage) => {
    try {
      const updateData = {
        current_stage: newStage,
        [STAGE_DATE_FIELD[newStage]]: today(),
      };
      const res = await axios.put(`${API}/pipeline/cases/${tinNumber}`, updateData, { withCredentials: true });
      const updated = res.data;
      setCases((prev) => prev.map((c) => (c.tin_number === tinNumber ? { ...c, ...updated } : c)));
      notifyCaseChanged({
        case_tin: tinNumber,
        action: "moved",
        to_stage: updated?.current_stage || newStage,
        patient: [updated?.name, updated?.surname].filter(Boolean).join(" ") || tinNumber,
      });
      if (updated?.current_stage !== newStage) {
        toast.warning(`Stage stays at ${updated?.current_stage} — later stage dates already exist`);
      } else {
        toast.success(`Moved to ${newStage}`);
      }
    } catch (error) {
      console.error("Error moving case:", error);
      toast.error(error.response?.data?.detail || "Failed to move case");
    }
  };

  // ---------- Context menu ----------
  const handleContextMenu = (e, c) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, caseItem: c });
  };

  const handleContextMenuAction = (action) => {
    if (!contextMenu) return;
    const c = contextMenu.caseItem;
    setContextMenu(null);
    if (action === "view") setExpandedCard(c.tin_number);
    if (action === "edit") openStageModal(c, c.current_stage);
    if (action === "edit-details") {
      if (c.current_stage === "Referrals") openReferralModal(c);
      else setEditingCase(c);
    }
    if (action === "cancel") cancelCase(c);
  };

  const cancelCase = async (c) => {
    if (!window.confirm(`Cancel case for ${c.name} ${c.surname}?`)) return;
    try {
      await axios.post(
        `${API}/pipeline/cases/${c.tin_number}/cancel`,
        { stage: c.current_stage, reason: "Cancelled from pipeline board" },
        { withCredentials: true }
      );
      setCases((prev) => prev.map((x) => (x.tin_number === c.tin_number ? { ...x, is_canceled: true } : x)));
      toast.success("Case cancelled");
    } catch (error) {
      console.error("Error cancelling case:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel case");
    }
  };

  const selectClass = "flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm";

  const activeFilterCount = [filterRep, filterMedicalAid, filterDoctor, filterArea, searchPatient, dateRangeStart, dateRangeEnd].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pipeline-page">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Layers className="h-7 w-7 text-blue-600" />
              Pipeline
            </h1>
            <p className="text-muted-foreground text-sm mt-1" data-testid="pipeline-subtitle">
              {pipelineType === "tt" ? "Titration (TT)" : "Sleep Study (SS)"} — {activeCases.length} active cases across {stages.length - 1} stages
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border bg-white p-1">
              {[["ss", "Sleep Study (SS)"], ["tt", "Titration (TT)"]].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSearchParams({ type: key })}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    pipelineType === key ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"
                  }`}
                  data-testid={`switch-pipeline-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          <Button
            onClick={() => openReferralModal(null)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-referral-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Referral
          </Button>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-lg border p-3" data-testid="stat-total">
            <p className="text-xs text-muted-foreground">Cases shown</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg border p-3" data-testid="stat-commission">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-3 w-3" /> Commission eligible
            </p>
            <p className="text-2xl font-bold text-emerald-600">{stats.commission}</p>
          </div>
          <div className="bg-white rounded-lg border p-3" data-testid="stat-overdue">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Overdue (&gt;30d)
            </p>
            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          </div>
          <div className="bg-white rounded-lg border p-3" data-testid="stat-cancelled">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Ban className="h-3 w-3" /> Cancelled
            </p>
            <p className="text-2xl font-bold text-gray-500">{stats.canceled}</p>
          </div>
        </div>
      </div>

      {/* Arrived from a dashboard number */}
      {focusStage && (
        <div className="mx-6 mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="stage-focus-banner">
          <Layers className="h-4 w-4 text-blue-600" />
          <p className="text-sm text-blue-900">
            Showing <span className="font-semibold">{focusStage}</span> only
            {doneMonth ? ` — completed in ${doneMonth}` : ""} ·{" "}
            {(casesByStage[focusStage] || []).length} referral(s)
          </p>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7 text-xs bg-white"
            onClick={() => setSearchParams({ type: pipelineType })}
            data-testid="clear-stage-focus"
          >
            Show all stages
          </Button>
        </div>
      )}

      {/* Sticky, collapsible filters */}
      <div className="bg-white border-y shadow-sm" data-testid="pipeline-filters">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
            {activeFilterCount > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium" data-testid="active-filter-count">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs" data-testid="clear-filters-button">
                Clear All
              </Button>
            )}
            <button
              onClick={() => setFiltersExpanded((v) => !v)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={filtersExpanded ? "Collapse filters" : "Expand filters"}
              data-testid="toggle-filters-button"
            >
              {filtersExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {filtersExpanded && (
          <div className="px-6 py-4 space-y-3 border-t" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Rep</Label>
                <select value={filterRep} onChange={(e) => setFilterRep(e.target.value)} className={selectClass} data-testid="filter-rep">
                  <option value="">All Reps</option>
                  {REP_DIRECTORY.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                  <option value="Unassigned">Unassigned</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Medical Aid</Label>
                <select value={filterMedicalAid} onChange={(e) => setFilterMedicalAid(e.target.value)} className={selectClass} data-testid="filter-medical-aid">
                  <option value="">All Aids</option>
                  {medicalAidOptions.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Doctor</Label>
                <select value={filterDoctor} onChange={(e) => setFilterDoctor(e.target.value)} className={selectClass} data-testid="filter-doctor">
                  <option value="">All Doctors</option>
                  {doctorOptions.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Area</Label>
                <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)} className={selectClass} data-testid="filter-area">
                  <option value="">All Areas</option>
                  {AREAS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Search Patient (Name or TIN)</Label>
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="e.g. John Smith or 12345"
                    value={searchPatient}
                    onChange={(e) => setSearchPatient(e.target.value)}
                    className="h-9 text-sm pl-7"
                    data-testid="filter-patient-search"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Referral date from</Label>
                <Input type="date" value={dateRangeStart} onChange={(e) => setDateRangeStart(e.target.value)} className="h-9 text-sm" data-testid="filter-date-start" />
              </div>
              <div>
                <Label className="text-xs">Referral date to</Label>
                <Input type="date" value={dateRangeEnd} onChange={(e) => setDateRangeEnd(e.target.value)} className="h-9 text-sm" data-testid="filter-date-end" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="flex items-center justify-center py-24" data-testid="pipeline-loading">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="px-6 py-6">
          <div
            ref={boardRef}
            className="overflow-x-auto overflow-y-hidden rounded-lg border bg-gray-50/60 p-4"
            onDragOver={(e) => edgeAutoScroll(e.clientX)}
            onDragLeave={stopAutoScroll}
            onDrop={stopAutoScroll}
            data-testid="kanban-scroll-container"
          >
          <div className="flex gap-4 min-w-max h-[65vh] min-h-[420px]">
            {visibleStages.map((stage) => {
              const stageCases = casesByStage[stage] || [];
              const colors = stageColors[stage] || ALL_STAGE_COLORS[stage] || STAGE_COLORS["Referrals"];
              const isOver = dragOverStage === stage;
              return (
                <div
                  key={stage}
                  className={`flex-shrink-0 ${focusStage ? "w-[min(92vw,640px)]" : "w-80"} h-full bg-white rounded-lg border-2 flex flex-col overflow-hidden transition-colors ${isOver ? "border-blue-500" : focusStage ? "border-blue-400 ring-2 ring-blue-100" : "border-gray-200"}`}
                  data-testid={`kanban-stage-${stage}`}
                >
                  <div className={`${colors.bg} ${colors.border} border-b-2 px-4 py-3 shrink-0`}>
                    <div className="flex items-center justify-between">
                      <h2 className={`font-semibold text-sm ${colors.text}`}>{stage}</h2>
                      <Badge className={`${colors.bg} ${colors.text}`} data-testid={`stage-count-${stage}`}>
                        {stageCases.length}
                      </Badge>
                    </div>
                  </div>

                  <div
                    className={`flex-1 min-h-0 p-3 overflow-y-auto space-y-3 ${isOver ? "bg-blue-50/60" : ""}`}
                    onDragOver={(e) => handleDragOver(e, stage)}
                    onDragLeave={() => setDragOverStage(null)}
                    onDrop={(e) => handleDrop(e, stage)}
                    data-stage-scroll={stage}
                    data-testid={`kanban-dropzone-${stage}`}
                  >
                    {stageCases.length === 0 ? (
                      <div className="text-center text-muted-foreground text-xs py-8 border border-dashed rounded-md">
                        No cases
                      </div>
                    ) : (
                      stageCases.map((c) => (
                        <PipelineCard
                          key={c.tin_number}
                          c={c}
                          editors={editorsFor(c.tin_number)}
                          onDragStart={(e) => handleDragStart(e, c)}
                          onDragEnd={() => {
                            stopAutoScroll();
                            setDraggedCase(null);
                          }}
                          onContextMenu={(e) => handleContextMenu(e, c)}
                          onOpen={() => {
                            // A case still in Referrals opens its referral form, pre-filled —
                            // staff never have to re-enter what was already captured
                            if (boardStage(c) === "Referral") {
                              openReferralModal(c);
                              return;
                            }
                            if (!openStageModal(c, c.current_stage)) setExpandedCard(c.tin_number);
                          }}
                          onView={(e) => {
                            e.stopPropagation();
                            setExpandedCard(c.tin_number);
                          }}
                          onEdit={(e) => {
                            e.stopPropagation();
                            if (!openStageModal(c, c.current_stage)) {
                              if (c.current_stage === "Referrals") openReferralModal(c);
                              else setEditingCase(c);
                            }
                          }}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Drag a card to move it straight to another stage · click a card or Edit to capture the stage form · scroll sideways for more stages.
          </p>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }} />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 w-44"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            data-testid="context-menu"
          >
            <button onClick={() => handleContextMenuAction("view")} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-100" data-testid="context-menu-view">
              <Eye className="h-3.5 w-3.5" />View Details
            </button>
            <button onClick={() => handleContextMenuAction("edit")} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-100" data-testid="context-menu-edit">
              <Edit className="h-3.5 w-3.5" />Edit Stage Form
            </button>
            <button onClick={() => handleContextMenuAction("edit-details")} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-100" data-testid="context-menu-edit-details">
              <Edit className="h-3.5 w-3.5" />Edit Case Details
            </button>
            <button onClick={() => handleContextMenuAction("cancel")} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-testid="context-menu-cancel">
              <XCircle className="h-3.5 w-3.5" />Cancel Case
            </button>
          </div>
        </>
      )}

      {showStageModal && currentStageConfig && (
        <StageModal
          isOpen={showStageModal}
          onClose={closeStageModal}
          stageConfig={currentStageConfig}
          caseData={stageModalCase}
          referralDate={stageModalCase?.referral_date}
          pipelineType={pipelineType}
          onDuplicateToTt={async () => {
            const res = await axios.post(
              `${API}/pipeline/cases/${stageModalCase.tin_number}/duplicate-to-tt`,
              {},
              { withCredentials: true }
            );
            toast.success(
              res.data?.created
                ? `Copied into Titration at SS Feedback as ${res.data.tin_number}`
                : `Already in TT as ${res.data?.tin_number}`
            );
            fetchCases();
          }}
          onSave={async (data, advanceTo = null) => {
            const target = advanceTo || targetStagePending || stageModalCase.current_stage;
            const payload = {
              [currentStageConfig.dataFieldName]: data,
              current_stage: target,
            };
            if (STAGE_DATE_FIELD[target]) payload[STAGE_DATE_FIELD[target]] = today();
            // Re-engaging a lost case clears the cancelled flag
            if (target !== "Lost/Cancelled" && stageModalCase.is_canceled) payload.is_canceled = false;
            const res = await axios.put(`${API}/pipeline/cases/${stageModalCase.tin_number}`, payload, {
              withCredentials: true,
            });
            if (target !== stageModalCase.current_stage && !advanceTo) {
              if (res.data?.current_stage === target) toast.success(`Moved to ${target}`);
              else toast.warning(`Stage stayed at ${res.data?.current_stage}`);
            }
            fetchCases();
            notifyCaseChanged({
              case_tin: stageModalCase.tin_number,
              action: target !== stageModalCase.current_stage ? "moved" : "updated",
              stage: target,
              to_stage: res.data?.current_stage || target,
              patient: [stageModalCase.name, stageModalCase.surname].filter(Boolean).join(" ") || stageModalCase.tin_number,
            });
            // Saving SS Follow up is where the modal offers the copy into Titration; no other
            // stage can create a duplicate.
          }}
          onMoveToLostCancelled={async (data) => {
            await axios.put(
              `${API}/pipeline/cases/${stageModalCase.tin_number}`,
              {
                [currentStageConfig.dataFieldName]: data,
                current_stage: "Lost/Cancelled",
                lost_cancelled_date: today(),
                lost_cancelled_data: {
                  cancellation_stage: stageModalCase.current_stage,
                  cancellation_date: today(),
                  cancellation_reason_final:
                    data.cancellation_reason || data.cancellation_reason_final || "",
                },
              },
              { withCredentials: true }
            );
            fetchCases();
          }}
        />
      )}

      <Dialog open={showTTDecisionModal} onOpenChange={setShowTTDecisionModal}>
        <DialogContent className="max-w-sm" data-testid="move-to-tt-dialog">
          <DialogHeader>
            <DialogTitle>Move to Titration (TT) Pipeline?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            {ttDecisionCase?.name} {ttDecisionCase?.surname} has reached SS Reports. Move them into the
            Titration pipeline for device setup and follow-up?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTTDecisionModal(false)} data-testid="decline-move-to-tt">
              No, end here
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-move-to-tt"
              onClick={async () => {
                try {
                  await axios.post(
                    `${API}/pipeline/cases/${ttDecisionCase.tin_number}/move-to-tt`,
                    {},
                    { withCredentials: true }
                  );
                  setShowTTDecisionModal(false);
                  toast.success("Case moved to the TT pipeline");
                  setSearchParams({ type: "tt" });
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to move to TT");
                }
              }}
            >
              Yes, move to TT
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showReferralModal && (
        <ReferralsStageModal
          caseData={referralModalCase}
          medicalAids={medicalAids}
          doctors={doctors}
          onClose={() => {
            setShowReferralModal(false);
            setReferralModalCase(null);
          }}
          onSaved={(saved) => {
            setShowReferralModal(false);
            setReferralModalCase(null);
            fetchCases();
            notifyCaseChanged({
              case_tin: saved?.tin_number || referralModalCase?.tin_number,
              action: referralModalCase ? "updated" : "created",
              patient:
                [saved?.name || referralModalCase?.name, saved?.surname || referralModalCase?.surname]
                  .filter(Boolean)
                  .join(" ") || saved?.tin_number || referralModalCase?.tin_number,
            });
          }}
        />
      )}

      {editingCase && (
        <EditCaseDialog
          caseItem={editingCase}
          medicalAids={medicalAids}
          doctors={doctors}
          onClose={() => setEditingCase(null)}
          onSaved={() => {
            const changed = editingCase;
            setEditingCase(null);
            fetchCases();
            notifyCaseChanged({
              case_tin: changed?.tin_number,
              action: "updated",
              patient: [changed?.name, changed?.surname].filter(Boolean).join(" ") || changed?.tin_number,
            });
          }}
        />
      )}

      {expandedCard && (
        <CaseDetailsModal
          caseItem={cases.find((c) => c.tin_number === expandedCard)}
          onClose={() => setExpandedCard(null)}
        />
      )}
    </div>
  );
};

const inputSelectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50";

const plansFor = (medicalAids, name) => medicalAids.find((m) => m.medical_aid_name === name)?.plans || [];

const LEAD_GENERATORS = ["Website", "Intercare", "Ecomed", "Facebook", "Medical reps"];

const doctorLabel = (d) => d.display_name || d.doctor_name || d.surname || "";

const ReferralsStageModal = ({ caseData = null, onClose, onSaved, medicalAids, doctors }) => {
  const isEdit = Boolean(caseData);
  const [form, setForm] = useState({
    lead_generator: caseData?.lead_generator || "",
    referral_date: caseData?.referral_date || today(),
    patient_name: caseData?.name || "",
    patient_surname: caseData?.surname || "",
    tin_number: caseData?.tin_number || "",
    doctor: caseData?.doctor || "",
    area: caseData?.area || "",
    assigned_rep: caseData?.assigned_rep_name || caseData?.assigned_rep || "",
    referral_from_dr_rooms: caseData?.referral_from_dr_rooms || "",
    out_of_hospital: caseData ? (caseData.out_of_hospital ? "Yes" : "No") : "",
  });
  const [selectedMedicalAid, setSelectedMedicalAid] = useState(caseData?.medical_aid || "");
  const [selectedPlan, setSelectedPlan] = useState(caseData?.medical_aid_plan || "");
  const [doctorSearch, setDoctorSearch] = useState(caseData?.doctor || "");
  const [showDoctorSearch, setShowDoctorSearch] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const filteredDoctors = doctorSearch.trim() === ""
    ? doctors.slice(0, 25)
    : doctors.filter((d) =>
        `${doctorLabel(d)} ${d.practice_name || ""}`.toLowerCase().includes(doctorSearch.toLowerCase())
      ).slice(0, 25);

  const validate = () => {
    const e = {};
    if (!isEdit && !form.lead_generator) e.lead_generator = "Lead generator is required";
    if (!form.referral_date) e.referral_date = "Referral date is required";
    if (!form.patient_name) e.patient_name = "Patient name is required";
    if (!form.patient_surname) e.patient_surname = "Patient surname is required";
    if (!form.tin_number) e.tin_number = "TIN number is required";
    if (!selectedMedicalAid) e.medical_aid_name = "Medical aid is required";
    if (!selectedPlan) e.medical_aid_plan = "Medical aid plan is required";
    if (!form.doctor) e.doctor = "Doctor is required";
    if (!form.area) e.area = "Area is required";
    if (!isEdit && !form.referral_from_dr_rooms) e.referral_from_dr_rooms = "Please specify if referral from Dr's rooms";
    if (!form.out_of_hospital) e.out_of_hospital = "Please specify if out of hospital";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const basePayload = () => ({
    name: form.patient_name,
    surname: form.patient_surname,
    doctor: form.doctor,
    area: form.area,
    assigned_rep: form.assigned_rep || undefined,
    medical_aid: selectedMedicalAid,
    medical_aid_plan: selectedPlan,
    out_of_hospital: form.out_of_hospital === "Yes",
    lead_generator: form.lead_generator,
    referral_from_dr_rooms: form.referral_from_dr_rooms,
  });

  const submit = async (targetStage) => {
    if (!validate()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(
          `${API}/pipeline/cases/${caseData.tin_number}`,
          {
            ...basePayload(),
            referral_date: form.referral_date,
            current_stage: targetStage || caseData.current_stage,
          },
          { withCredentials: true }
        );
        toast.success(targetStage === "Pre SS" ? "Moved to Pre SS" : "Referral saved");
      } else {
        await axios.post(
          `${API}/pipeline/referrals`,
          { ...basePayload(), date: form.referral_date, tin_number: form.tin_number },
          { withCredentials: true }
        );
        toast.success("Referral created");
      }
      onSaved({ tin_number: form.tin_number, name: form.name, surname: form.surname });
    } catch (error) {
      console.error("Error saving referral:", error);
      toast.error(error.response?.data?.detail || "Failed to save referral");
    } finally {
      setSaving(false);
    }
  };

  const errorClass = (key) => (errors[key] ? "border-red-500" : "");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="referrals-stage-modal">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit Referral — ${caseData.name} ${caseData.surname}` : "New Referral"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Lead Generator {isEdit ? "" : "*"}</Label>
            <select
              value={form.lead_generator}
              onChange={(e) => set("lead_generator", e.target.value)}
              className={`${inputSelectClass} ${errorClass("lead_generator")}`}
              data-testid="referral-lead-generator"
            >
              <option value="">Select lead generator...</option>
              {LEAD_GENERATORS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {errors.lead_generator && <p className="text-xs text-red-500 mt-1">{errors.lead_generator}</p>}
          </div>

          <div>
            <Label className="text-sm font-medium">Date Referral Received *</Label>
            <Input
              type="date"
              value={form.referral_date}
              onChange={(e) => set("referral_date", e.target.value)}
              disabled={isEdit}
              className={`${errorClass("referral_date")} disabled:opacity-60`}
              data-testid="referral-date"
            />
            {isEdit && (
              <p className="text-xs text-muted-foreground mt-1">
                The referral date is fixed once the case exists
              </p>
            )}
            {errors.referral_date && <p className="text-xs text-red-500 mt-1">{errors.referral_date}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Patient Name (from GoodX) *</Label>
              <Input
                value={form.patient_name}
                onChange={(e) => set("patient_name", e.target.value)}
                placeholder="Copy and paste from GoodX"
                className={errorClass("patient_name")}
                data-testid="referral-patient-name"
              />
              {errors.patient_name && <p className="text-xs text-red-500 mt-1">{errors.patient_name}</p>}
            </div>
            <div>
              <Label className="text-sm font-medium">Patient Surname (from GoodX) *</Label>
              <Input
                value={form.patient_surname}
                onChange={(e) => set("patient_surname", e.target.value)}
                placeholder="Copy and paste from GoodX"
                className={errorClass("patient_surname")}
                data-testid="referral-patient-surname"
              />
              {errors.patient_surname && <p className="text-xs text-red-500 mt-1">{errors.patient_surname}</p>}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">TIN Number (from GoodX) *</Label>
            <Input
              value={form.tin_number}
              onChange={(e) => set("tin_number", e.target.value)}
              placeholder="Copy and paste from GoodX"
              disabled={isEdit}
              className={`${errorClass("tin_number")} disabled:opacity-60`}
              data-testid="referral-tin-number"
            />
            {isEdit && <p className="text-xs text-muted-foreground mt-1">TIN cannot be changed on an existing case</p>}
            {errors.tin_number && <p className="text-xs text-red-500 mt-1">{errors.tin_number}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Medical Aid *</Label>
              <select
                value={selectedMedicalAid}
                onChange={(e) => {
                  setSelectedMedicalAid(e.target.value);
                  setSelectedPlan("");
                }}
                className={`${inputSelectClass} ${errorClass("medical_aid_name")}`}
                data-testid="referral-medical-aid"
              >
                <option value="">Select medical aid...</option>
                {medicalAids.map((m) => (
                  <option key={m.id} value={m.medical_aid_name}>{m.medical_aid_name}</option>
                ))}
              </select>
              {errors.medical_aid_name && <p className="text-xs text-red-500 mt-1">{errors.medical_aid_name}</p>}
            </div>
            <div>
              <Label className="text-sm font-medium">Medical Aid Plan *</Label>
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                disabled={!selectedMedicalAid}
                className={`${inputSelectClass} ${errorClass("medical_aid_plan")}`}
                data-testid="referral-medical-aid-plan"
              >
                <option value="">
                  {!selectedMedicalAid
                    ? "Select medical aid first..."
                    : plansFor(medicalAids, selectedMedicalAid).length === 0
                    ? "No plans available"
                    : "Select plan..."}
                </option>
                {plansFor(medicalAids, selectedMedicalAid).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {errors.medical_aid_plan && <p className="text-xs text-red-500 mt-1">{errors.medical_aid_plan}</p>}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Doctor That Referred Patient *</Label>
            <div className="relative">
              <Input
                placeholder="Search doctor in CRM..."
                value={doctorSearch}
                onChange={(e) => {
                  setDoctorSearch(e.target.value);
                  setShowDoctorSearch(true);
                }}
                onFocus={() => setShowDoctorSearch(true)}
                className={errorClass("doctor")}
                data-testid="referral-doctor-search"
              />
              {showDoctorSearch && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDoctorSearch(false)} />
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-lg z-50 max-h-56 overflow-y-auto">
                    {filteredDoctors.map((d) => (
                      <button
                        key={d.doctor_id}
                        type="button"
                        onClick={() => {
                          set("doctor", doctorLabel(d));
                          if (d.area) set("area", d.area);
                          if (d.assigned_rep && REP_DIRECTORY.includes(d.assigned_rep)) set("assigned_rep", d.assigned_rep);
                          setDoctorSearch(doctorLabel(d));
                          setShowDoctorSearch(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-b-0"
                        data-testid={`doctor-option-${d.doctor_id}`}
                      >
                        <p className="font-medium">{doctorLabel(d)}</p>
                        <p className="text-xs text-gray-500">{d.practice_name} • {d.area}</p>
                      </button>
                    ))}
                    <div className="px-3 py-3 text-center border-t bg-gray-50">
                      {filteredDoctors.length === 0 && (
                        <p className="text-sm text-gray-500 mb-2">Doctor not in CRM</p>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        type="button"
                        onClick={() => {
                          toast.info("Opening Doctors page in a new tab");
                          window.open("/doctors", "_blank");
                        }}
                        className="w-full"
                        data-testid="add-new-doctor-button"
                      >
                        <Plus className="h-3 w-3 mr-1" />Add New Doctor
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            {form.doctor && <p className="text-xs text-emerald-600 mt-1">Selected: {form.doctor}</p>}
            {errors.doctor && <p className="text-xs text-red-500 mt-1">{errors.doctor}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Area *</Label>
              <select
                value={form.area}
                onChange={(e) => set("area", e.target.value)}
                className={`${inputSelectClass} ${errorClass("area")}`}
                data-testid="referral-area"
              >
                <option value="">Select area...</option>
                {AREAS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
              {errors.area && <p className="text-xs text-red-500 mt-1">{errors.area}</p>}
            </div>
            <div>
              <Label className="text-sm font-medium">Assigned Rep</Label>
              <select
                value={form.assigned_rep}
                onChange={(e) => set("assigned_rep", e.target.value)}
                className={inputSelectClass}
                data-testid="referral-assigned-rep"
              >
                <option value="">Unassigned</option>
                {REP_DIRECTORY.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Referral Sent from Dr's Rooms? {isEdit ? "" : "*"}</Label>
              <select
                value={form.referral_from_dr_rooms}
                onChange={(e) => set("referral_from_dr_rooms", e.target.value)}
                className={`${inputSelectClass} ${errorClass("referral_from_dr_rooms")}`}
                data-testid="referral-from-rooms"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.referral_from_dr_rooms && <p className="text-xs text-red-500 mt-1">{errors.referral_from_dr_rooms}</p>}
            </div>
            <div>
              <Label className="text-sm font-medium">Out of Hospital? *</Label>
              <select
                value={form.out_of_hospital}
                onChange={(e) => set("out_of_hospital", e.target.value)}
                className={`${inputSelectClass} ${errorClass("out_of_hospital")}`}
                data-testid="referral-out-of-hospital"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.out_of_hospital && <p className="text-xs text-red-500 mt-1">{errors.out_of_hospital}</p>}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="referral-modal-cancel">
            Cancel
          </Button>
          <Button variant="outline" onClick={() => submit(null)} disabled={saving} data-testid="referral-modal-save">
            {saving ? "Saving..." : "Save"}
          </Button>
          {isEdit && (
            <Button
              onClick={() => submit("Pre SS")}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="referral-modal-move-to-next"
            >
              {saving ? "Moving..." : "Move to Pre SS"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditCaseDialog = ({ caseItem, onClose, onSaved, medicalAids, doctors }) => {
  const [form, setForm] = useState({
    name: caseItem.name || "",
    surname: caseItem.surname || "",
    doctor: caseItem.doctor || "",
    area: caseItem.area || "",
    assigned_rep: caseItem.assigned_rep_name || caseItem.assigned_rep || "",
    medical_aid: caseItem.medical_aid || "",
    current_stage: caseItem.current_stage || "Referrals",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.surname) {
      toast.error("Patient name and surname are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (form.current_stage !== caseItem.current_stage) {
        payload[STAGE_DATE_FIELD[form.current_stage]] = today();
      }
      const res = await axios.put(`${API}/pipeline/cases/${caseItem.tin_number}`, payload, { withCredentials: true });
      if (res.data?.current_stage !== form.current_stage) {
        toast.warning(`Saved — stage resolved to ${res.data?.current_stage} based on stage dates`);
      } else {
        toast.success("Case updated");
      }
      onSaved();
    } catch (error) {
      console.error("Error updating case:", error);
      toast.error(error.response?.data?.detail || "Failed to update case");
    } finally {
      setSubmitting(false);
    }
  };

  const doctorNames = Array.from(new Set([
    ...doctors.map((d) => d.display_name || d.doctor_name).filter(Boolean),
    ...(form.doctor ? [form.doctor] : []),
  ])).sort();

  const aidNames = Array.from(new Set([
    ...medicalAids.map((m) => m.medical_aid_name),
    ...(form.medical_aid ? [form.medical_aid] : []),
  ])).sort();

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-referral-dialog">
        <DialogHeader>
          <DialogTitle>Edit Case — {caseItem.tin_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Patient Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-referral-name" />
            </div>
            <div>
              <Label className="text-xs">Patient Surname *</Label>
              <Input value={form.surname} onChange={(e) => set("surname", e.target.value)} data-testid="edit-referral-surname" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Doctor</Label>
            <select value={form.doctor} onChange={(e) => set("doctor", e.target.value)} className={inputSelectClass} data-testid="edit-referral-doctor">
              <option value="">Select Doctor</option>
              {doctorNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Medical Aid</Label>
            <select value={form.medical_aid} onChange={(e) => set("medical_aid", e.target.value)} className={inputSelectClass} data-testid="edit-referral-medical-aid">
              <option value="">None</option>
              {aidNames.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Area</Label>
              <select value={form.area} onChange={(e) => set("area", e.target.value)} className={inputSelectClass} data-testid="edit-referral-area">
                {AREAS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Assigned Rep</Label>
              <select value={form.assigned_rep} onChange={(e) => set("assigned_rep", e.target.value)} className={inputSelectClass} data-testid="edit-referral-rep">
                <option value="">Unassigned</option>
                {REP_DIRECTORY.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Stage</Label>
            <select value={form.current_stage} onChange={(e) => set("current_stage", e.target.value)} className={inputSelectClass} data-testid="edit-referral-stage">
              {PIPELINE_STAGES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-referral-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-referral-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CaseDetailsModal = ({ caseItem, onClose }) => {
  if (!caseItem) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="referral-details-modal">
        <DialogHeader>
          <DialogTitle>
            {caseItem.name} {caseItem.surname}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Detail label="TIN" value={caseItem.tin_number} />
            <Detail label="Medical Aid" value={caseItem.medical_aid || "—"} />
            <Detail label="Plan" value={caseItem.medical_aid_plan || "—"} />
            <Detail label="Doctor" value={caseItem.doctor || "—"} />
            <Detail label="Rep" value={repName(caseItem)} />
            <Detail label="Area" value={caseItem.area || "—"} />
            <Detail label="Referral Date" value={fmt(caseItem.referral_date)} />
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${STAGE_COLORS[caseItem.current_stage]?.bg} ${STAGE_COLORS[caseItem.current_stage]?.text}`}>
              {caseItem.current_stage}
            </Badge>
            <span className="text-xs text-muted-foreground">{getDaysInStage(caseItem)} days in stage</span>
            {caseItem.commission_eligible && <Badge className="bg-emerald-100 text-emerald-800">Commission eligible</Badge>}
            {caseItem.is_canceled && <Badge variant="destructive">Cancelled</Badge>}
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Stage timeline</p>
            <div className="space-y-1">
              {PIPELINE_STAGES.map((s) => {
                const d = caseItem[STAGE_DATE_FIELD[s]];
                return (
                  <div key={s} className="flex items-center justify-between text-sm border-b py-1.5">
                    <span className={d ? "font-medium" : "text-muted-foreground"}>{s}</span>
                    <span className={d ? "text-gray-900" : "text-muted-foreground"}>{fmt(d)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} data-testid="referral-details-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <p className="font-semibold text-sm">{value}</p>
  </div>
);

export default PipelinePage;
